import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { User } from '../lib/types';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('tekizai_token');
    if (!token) {
      setState({ user: null, loading: false, error: null });
      return;
    }

    try {
      const { user } = await api.auth.me();
      setState({ user: user as unknown as User, loading: false, error: null });
    } catch {
      localStorage.removeItem('tekizai_token');
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const { token, user } = await api.auth.login({ email, password });
      localStorage.setItem('tekizai_token', token);
      setState({ user: user as unknown as User, loading: false, error: null });
      return true;
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'ログインに失敗しました',
      }));
      return false;
    }
  };

  const register = async (companyName: string, email: string, password: string, name: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const { token, user } = await api.auth.register({
        company_name: companyName,
        email,
        password,
        name,
      });
      localStorage.setItem('tekizai_token', token);
      setState({ user: user as unknown as User, loading: false, error: null });
      return true;
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : '登録に失敗しました',
      }));
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('tekizai_token');
    setState({ user: null, loading: false, error: null });
  };

  return {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    register,
    logout,
    checkAuth,
  };
}
